import { Button, Modal, confirm } from "@devhop/ui";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { PlusIcon, HelpCircle } from "lucide-react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { useDisclosure } from "@/shared/hooks/useDisclosure";
import type { OffsetPageQueryDTO } from "@/shared/contracts/base";
import {
  useAuditIssuesQuery,
  useCreateAuditIssue,
  useUpdateAuditIssue,
  useDeleteAuditIssue,
  useAuditIssueQuery,
} from "../api/queries";
import { auditIssuesApi } from "../api/client";
import { useQueryClient } from "@tanstack/react-query";
import { auditIssueKeys } from "../api/queries";
import { toast } from "@devhop/ui";
import { AuditIssueTable } from "../ui/AuditIssueTable";
import { AuditIssueFilter } from "../ui/AuditIssueFilter";
import { AuditIssueForm } from "../ui/AuditIssueForm";
import { AuditIssueDetailModal } from "../ui/AuditIssueDetailModal";
import type { CreateAuditIssueDTO, UpdateAuditIssueDTO } from "@/modules/internal-audit/domain/contracts";
import { AuditIssueListTour, useAuditIssueListTour } from "../tour/AuditIssueListTour";

export function AuditIssueListPage() {
  const nav = useNavigate({ from: "/app/internal-audit/issues" });
  const search = useSearch({ from: "/app/internal-audit/issues" }) as OffsetPageQueryDTO;
  const queryClient = useQueryClient();
  const tour = useAuditIssueListTour();

  const { data, isLoading } = useAuditIssuesQuery({
    limit: search.limit ?? 20,
    offset: search.offset ?? 0,
    sort: search.sort,
    filters: search.filters,
  });

  const createModal = useDisclosure();
  const editModal = useDisclosure<{ id: string }>();
  const detailModal = useDisclosure<{ id: string }>();

  const createMutation = useCreateAuditIssue();
  const updateMutation = useUpdateAuditIssue(editModal.data?.id || "");
  const deleteMutation = useDeleteAuditIssue();
  const { data: detailIssue } = useAuditIssueQuery(detailModal.data?.id || "");
  const { data: editIssue } = useAuditIssueQuery(editModal.data?.id || "");

  const handlePaginationChange = (offset: number, limit: number) => {
    nav({
      search: {
        offset,
        limit,
        sort: search.sort,
        filters: search.filters,
      },
    });
  };

  const handleSortingChange = (id: string, desc: boolean) => {
    nav({
      search: {
        offset: 0,
        limit: search.limit ?? 20,
        sort: [{ field: id, dir: desc ? "desc" : "asc" }],
        filters: search.filters,
      },
    });
  };

  const handleView = (id: string) => {
    detailModal.openWith({ id });
  };

  const handleEdit = (id: string) => {
    editModal.openWith({ id });
  };

  const handleDelete = async (id: string) => {
    const ok = await confirm({
      title: "ລຶບບັນຫາການກວດສອບ",
      description: "ທ່ານແນ່ໃຈບໍ່ວ່າຕ້ອງການລຶບບັນຫານີ້? ການກະທຳນີ້ບໍ່ສາມາດຍົກເລີກໄດ້.",
      actionText: "ລຶບ",
      cancelText: "ຍົກເລີກ",
      ActionProps: {
        variant: "destructive",
      },
    });

    if (!ok) return;

    try {
      await deleteMutation.mutateAsync(id);
    } catch {
      // Error toast already handled by mutation
    }
  };

  const handleClose = async (id: string) => {
    // Check current issue status before attempting to close
    const currentIssue = data?.data.find((issue) => issue.id === id);
    if (currentIssue?.status === "closed") {
      toast.error("ບັນຫານີ້ຖືກປິດແລ້ວ");
      return;
    }

    const ok = await confirm({
      title: "ປິດບັນຫາ",
      description: "ທ່ານແນ່ໃຈບໍ່ວ່າຕ້ອງການປິດບັນຫານີ້? ຫຼັງຈາກປິດແລ້ວຈະບໍ່ສາມາດແກ້ໄຂໄດ້.",
      actionText: "ປິດ",
      cancelText: "ຍົກເລີກ",
    });

    if (!ok) return;

    try {
      await auditIssuesApi.update(id, {
        status: "closed",
      } as UpdateAuditIssueDTO);
      queryClient.invalidateQueries({ queryKey: auditIssueKeys.all });
      queryClient.invalidateQueries({ queryKey: auditIssueKeys.detail(id) });
      toast.success("ປິດບັນຫາສຳເລັດແລ້ວ");
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error && error.message === "ISSUE_CLOSED"
          ? "ບັນຫານີ້ຖືກປິດແລ້ວ"
          : "ບໍ່ສາມາດປິດບັນຫາໄດ້";
      toast.error(errorMessage);
      console.error(error);
    }
  };


  const sortBy = search.sort?.[0]?.field;
  const sortOrder = search.sort?.[0]?.dir === "desc" ? "desc" : "asc";

  return (
    <>
      <Header />
      <Main>
        <AuditIssueListTour
          run={tour.run}
          onCallback={tour.handleJoyrideCallback}
        />
        <div className="mb-4">
          <div className="flex items-center justify-between" data-tourid="issue-toolbar">
            <div>
              <div className="flex items-center space-x-2">
                <h1 className="font-bold text-2xl">ບັນຫາການກວດສອບ</h1>
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-7 rounded-full"
                  onClick={tour.startTour}
                >
                  <HelpCircle className="size-5" />
                </Button>
              </div>
              <p className="text-muted-foreground">
                ຈັດການບັນຫາແລະການພົບເຫັນຈາກການກວດສອບ
              </p>
            </div>
            <Button
              onClick={() => createModal.open()}
              className="gap-2"
            >
              <PlusIcon className="h-4 w-4" />
              ເພີ່ມບັນຫາໃໝ່
            </Button>
          </div>
        </div>

        <AuditIssueFilter />

        <div data-tourid="issue-table">
          <AuditIssueTable
            data={data?.data ?? []}
            isLoading={isLoading}
            offset={search.offset ?? 0}
            limit={search.limit ?? 20}
            totalCount={data?.meta.total ?? 0}
            onPaginationChange={handlePaginationChange}
            sortBy={sortBy}
            sortOrder={sortOrder}
            onSortingChange={handleSortingChange}
            onView={handleView}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onClose={handleClose}
          />
        </div>

        <Modal
          open={!!createModal.isOpen}
          onOpenChange={createModal.toggle}
          title="ເພີ່ມບັນຫາໃໝ່"
          size="lg"
        >
          <div className="max-h-[calc(100vh-12rem)] overflow-y-auto overflow-x-hidden">
            <AuditIssueForm
              onSubmit={async (vals) => {
                await createMutation.mutateAsync(vals as CreateAuditIssueDTO);
                createModal.close();
              }}
              submitting={createMutation.isPending}
            />
          </div>
        </Modal>

        <Modal
          open={!!editModal.isOpen}
          onOpenChange={editModal.toggle}
          title="ແກ້ໄຂບັນຫາ"
          size="lg"
        >
          {editIssue && (
            <>
              {editIssue.status === "closed" ? (
                <div className="py-8 text-center">
                  <p className="text-muted-foreground">
                    ບັນຫານີ້ຖືກປິດແລ້ວ ບໍ່ສາມາດແກ້ໄຂໄດ້
                  </p>
                </div>
              ) : (
                <div className="max-h-[calc(100vh-12rem)] overflow-y-auto overflow-x-hidden">
                  <AuditIssueForm
                    initialValues={editIssue}
                    onSubmit={async (vals) => {
                      if (!editModal.data?.id) return;
                      await updateMutation.mutateAsync(vals as UpdateAuditIssueDTO);
                      editModal.close();
                    }}
                    submitting={updateMutation.isPending}
                  />
                </div>
              )}
            </>
          )}
        </Modal>

        <AuditIssueDetailModal
          open={!!detailModal.isOpen}
          onOpenChange={detailModal.toggle}
          issue={detailIssue || null}
          onClose={detailIssue && detailIssue.status !== "closed" ? () => handleClose(detailIssue.id) : undefined}
        />
      </Main>
    </>
  );
}

